import { Spacing } from '@persona-id/design-tokens';
import { MoreIcon, PlusIcon } from '@persona-id/design-tokens/icons';
import { MouseEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ClickableDetail from 'dashboard/components/ClickableDetail';
import { PageTabBar, TabbedSectionContainer } from 'dashboard/components/TabbedSectionV2/shared';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Account } from 'dashboard/models/account';
import { AccountStatus } from 'dashboard/models/account-status';
import { AccountType } from 'dashboard/models/account-type';
import { CaseDataManager } from 'dashboard/models/case';
import { SARNarrativeTemplate } from 'dashboard/models/case-template';
import { ProductFeatureName } from 'dashboard/models/organization';
import { SAR, SARData, SARStatus } from 'dashboard/models/sar';
import { Tag } from 'dashboard/models/tag';
import { Transaction } from 'dashboard/models/transaction';
import { TransactionStatus } from 'dashboard/models/transaction-status';
import { TransactionType } from 'dashboard/models/transaction-type';
import Placeholder from 'dashboard/modules/Placeholder';
import LayoutTable from 'dashboard/modules/Resource/Layouts/LayoutTable';
import { generateTableParams } from 'dashboard/modules/Resource/Resource';
import ResourceAccountV2 from 'dashboard/modules/ResourceAccount/ResourceAccountV2';
import { ResourceTransactionAttribute } from 'dashboard/modules/ResourceTransaction/model';
import { showMessage } from 'dashboard/redux/actions/messages';
import { useGetSarsByIdsQuery } from 'dashboard/redux/api/sar';
import { RootState } from 'dashboard/redux/store';
import { validateSAR } from 'dashboard/redux/thunk-actions/sar';
import { JSONFormSAR } from 'dashboard/views/DashboardSARShowView/FormSAR';
import SARStatusSticker from 'dashboard/views/DashboardSARsView/SARStatusSticker';
import Badge from 'design-system/components/Badge';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Header from 'design-system/components/Card/Header/Header';
import DropdownMenu from 'design-system/components/DropdownMenu';
import { Tab } from 'design-system/components/TabBar';
import { Paragraph } from 'design-system/components/Text';
import Tooltip, { HelperTooltip } from 'design-system/components/Tooltip';
import { FontWeight } from 'design-system/variables/text';
import { SessionStorageKey } from 'lib/clients/StorageClient';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import usePermission from 'lib/hooks/usePermission';
import useProductFeature from 'lib/hooks/useProductFeature';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import { useSessionStorageMap } from 'lib/hooks/useSessionStorageMap';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledLink = styled(Link)`
  text-decoration: underline;
  font-weight: ${FontWeight.Regular};
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
`;

const StyledCard = styled(Card)`
  margin-bottom: 0;
`;

const NullStateContainer = styled(Card.Body)`
  padding-top: 0;
`;

const StyledPageTabBar = styled(PageTabBar)`
  padding: 0 ${Spacing[5]};
  margin-bottom: 0;
`;

const TabContentContainer = styled.div<{ isHidden: boolean }>`
  display: ${({ isHidden }) => (isHidden ? 'none' : 'default')};
`;

export interface SARModuleProps {
  sarTokens: string[];
  disableInteraction: boolean;
  sarNarrativeTemplates: SARNarrativeTemplate[];
  caseDataManager: CaseDataManager;
  handleCreateSAR: () => Promise<void>;
  handleUpdateSAR: (sarId: string, sarData: SARData) => Promise<void>;
  handleAutofillSAR: (sarId: string) => Promise<void>;
}

export const SARModule = ({ sarTokens, handleUpdateSAR, ...restProps }: SARModuleProps) => {
  const dispatch = useDispatch();

  const validationErrors = useSelector((state: RootState) => state.sar.validationErrors);
  const {
    data: responses,
    error,
    isLoading,
    refetch,
  } = useGetSarsByIdsQuery(sarTokens, {
    refetchOnMountOrArgChange: true,
  });

  const handleUpdateAndRefetch = async (sarId: string, sarData: SARData) => {
    await handleUpdateSAR(sarId, sarData);
    await refetch();
  };

  useRTKErrorHandler({
    error,
    onError: () => {
      return {
        message: 'Failed to load Sars.',
        level: MessageType.Failure,
      };
    },
  });

  const { error: validateSARError, callback: handleValidateSAR } = useApiCallback(
    async (sarToken: string) => {
      dispatch(validateSAR(sarToken));
    },
    [],
  );

  useApiErrorHandler({
    error: validateSARError,
    onError: () =>
      dispatch(
        showMessage(
          'Something went wrong while running validations. Please try again.',
          MessageType.Failure,
        ),
      ),
    onNetworkError: () =>
      dispatch(
        showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
      ),
  });

  if (isLoading) {
    return <LoadingIndicator />;
  }

  const sarsData = (responses || []).map((data) => {
    const { data: sar, included } = data || {};
    const transactions = included?.filter((t) => t.type === ApiModel.Transaction) as Transaction[];
    const transactionStatuses = included?.filter(
      (ts) => ts.type === ApiModel.TransactionStatus,
    ) as TransactionStatus[];
    const transactionTypes = included?.filter(
      (tt) => tt.type === ApiModel.TransactionType,
    ) as TransactionType[];
    const transactionTags = included?.filter((tt) => tt.type === ApiModel.Tag) as Tag[];
    const accounts = included?.filter((a) => a.type === ApiModel.Account) as Account[];
    const accountStatuses = included?.filter(
      (ts) => ts.type === ApiModel.AccountStatus,
    ) as AccountStatus[];
    const accountTypes = included?.filter(
      (tt) => tt.type === ApiModel.AccountType,
    ) as AccountType[];

    return {
      sar,
      validationErrors: validationErrors[sar.id],
      transactions,
      transactionStatuses,
      transactionTypes,
      transactionTags,
      accounts,
      accountStatuses,
      accountTypes,
    };
  });

  return (
    <SARList
      {...restProps}
      sarsData={sarsData}
      validationErrors={validationErrors}
      handleValidateSAR={handleValidateSAR}
      handleUpdateSAR={handleUpdateAndRefetch}
    />
  );
};

interface SARsModuleData {
  sar: SAR;
  transactions: Transaction[];
  transactionStatuses: TransactionStatus[];
  transactionTypes: TransactionType[];
  transactionTags: Tag[];
  accounts: Account[];
  accountStatuses: AccountStatus[];
  accountTypes: AccountType[];
}

export interface SARListProps {
  sarsData?: SARsModuleData[];
  caseDataManager: CaseDataManager;
  sarNarrativeTemplates: SARNarrativeTemplate[];
  validationErrors?: Record<string, string[]>;
  disableInteraction: boolean;
  handleCreateSAR: () => Promise<void>;
  handleUpdateSAR: (sarId: string, sarData: SARData) => Promise<void>;
  handleAutofillSAR: (sarId: string) => Promise<void>;
  handleValidateSAR?: (sarId: string) => Promise<void>;
}

export const SARList = ({
  sarsData = [],
  caseDataManager = {},
  sarNarrativeTemplates,
  disableInteraction,
  handleCreateSAR,
  handleUpdateSAR,
  handleAutofillSAR,
  handleValidateSAR,
}: SARListProps) => {
  const isSAREnabled = useProductFeature(ProductFeatureName.SARsDashboard);
  const hasSARCreatePermissions = usePermission(Permission.SARCreate);

  if (!isSAREnabled) {
    return (
      <Placeholder
        title='Suspicious Activity Report'
        message='Suspicious Activity Report feature not enabled.'
      />
    );
  }

  if (sarsData.length === 0) {
    return (
      <StyledCard>
        <Card.Body>
          <Card.Title
            actions={
              <>
                <Button
                  id='sar-module__create-sar'
                  variant='basic'
                  disabled={disableInteraction || !hasSARCreatePermissions}
                  prefixIcon={<PlusIcon />}
                  onClick={handleCreateSAR}
                >
                  Create SAR
                </Button>
                {!hasSARCreatePermissions && (
                  <HelperTooltip>Your role does not have permission to create SARs.</HelperTooltip>
                )}
              </>
            }
          >
            Suspicious Activity Report
          </Card.Title>
        </Card.Body>
        <NullStateContainer>
          <Paragraph>Create a SAR to document, validate and file your investigation.</Paragraph>
        </NullStateContainer>
      </StyledCard>
    );
  }

  return (
    <>
      {sarsData.map((sarData) => (
        <PureSARModule
          key={sarData.sar.id}
          {...sarData}
          sarNarrativeTemplates={sarNarrativeTemplates}
          caseDataManager={caseDataManager}
          disableInteraction={disableInteraction}
          handleCreateSAR={handleCreateSAR}
          handleUpdateSAR={handleUpdateSAR}
          handleAutofillSAR={handleAutofillSAR}
          handleValidateSAR={handleValidateSAR}
        />
      ))}
    </>
  );
};

export interface PureSARModuleProps {
  sar: SAR;
  caseDataManager: CaseDataManager;
  validationErrors?: string[];
  transactions?: Transaction[];
  transactionStatuses?: TransactionStatus[];
  transactionTypes?: TransactionType[];
  transactionTags?: Tag[];
  accounts?: Account[];
  accountStatuses?: AccountStatus[];
  accountTypes?: AccountType[];
  sarNarrativeTemplates: SARNarrativeTemplate[];
  disableInteraction: boolean;
  handleCreateSAR: () => Promise<void>;
  handleUpdateSAR: (sarId: string, sarData: SARData) => Promise<void>;
  handleAutofillSAR: (sarId: string) => Promise<void>;
  handleValidateSAR?: (sarId: string) => Promise<void>;
}

export const PureSARModule = ({
  sar,
  caseDataManager,
  validationErrors = [],
  transactions = [],
  transactionStatuses = [],
  transactionTypes = [],
  transactionTags = [],
  accounts = [],
  accountStatuses = [],
  accountTypes = [],
  sarNarrativeTemplates,
  handleUpdateSAR,
  handleAutofillSAR,
  handleValidateSAR,
}: PureSARModuleProps) => {
  const [selectedTabIdx, setSelectedTabIdx] = useSessionStorageMap<number>(
    SessionStorageKey.SarModuleTabIdx,
    sar.id,
    0,
  );

  const hasSARWritePermission = usePermission(Permission.SARWrite);
  const isReadonly = !!sar.attributes['filed-at'] || !!sar.attributes['archived-at'];

  return (
    // TODO: prevent navigation away from the page if there are unsaved changes
    <JSONFormSAR
      scope={sar.id}
      sar={sar}
      sarNarrativeTemplates={sarNarrativeTemplates}
      caseDataManager={caseDataManager}
      onAsyncValidate={async () => handleValidateSAR?.(sar.id)}
      additionalValidationErrors={validationErrors}
      onSubmit={async (sarData: SARData) => handleUpdateSAR(sar.id, sarData)}
    >
      {({ renderedForm, isDirty, submitForm, validateForm }) => {
        const renderTransactionsTab = () => {
          if (transactions.length === 0) {
            return <Placeholder title='Transactions' message='No transactions found.' />;
          }
          const {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            cardTitle,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            nullStateText,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            defaultVisibleKeys,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            columnIdToLabel,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            columnDefs,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            tableData,
          } = generateTableParams({
            resources: transactions,
            tags: transactionTags,
            transactionStatuses,
            transactionTypes,
            defaultVisibleKeys: Object.values(ResourceTransactionAttribute),
          });

          return (
            <LayoutTable
              cardTitle={cardTitle}
              nullStateText={nullStateText}
              defaultVisibleKeys={defaultVisibleKeys}
              columnIdToLabel={columnIdToLabel}
              columnDefs={columnDefs}
              tableData={tableData}
            />
          );
        };

        const renderAccountsTab = () => {
          if (accounts.length === 0) {
            return <Placeholder title='Accounts' message='No accounts found.' />;
          }
          return (
            <>
              {accounts.map((account) => {
                const accountStatus = accountStatuses.find(
                  (obj) => obj.id === account.relationships['account-status']?.data.id,
                );
                const accountType = accountTypes.find(
                  (obj) => obj.id === account.relationships['account-type']?.data.id,
                );
                return (
                  <ResourceAccountV2
                    key={account.id}
                    account={account}
                    accountStatus={accountStatus}
                    accountType={accountType}
                  />
                );
              })}
            </>
          );
        };

        const tabs = [
          {
            name: 'Form',
            contents: renderedForm,
          },
          {
            name: 'Transactions',
            badge: <Badge>{transactions.length}</Badge>,
            contents: renderTransactionsTab(),
          },
          {
            name: 'Accounts',
            badge: <Badge>{accounts.length}</Badge>,
            contents: renderAccountsTab(),
          },
        ];

        return (
          <ClickableDetail
            isDefaultOpen={sar.attributes.status === SARStatus.Open}
            header={
              <HeaderContainer>
                <div
                  css={`
                    display: flex;
                    align-items: center;
                    gap: ${Spacing[2]};
                  `}
                >
                  {/* wrapping header in span is a hack to prevent bottom margin */}
                  <span>
                    <Header>{sar.attributes.name}</Header>
                  </span>
                  <SARStatusSticker status={sar.attributes.status} />
                </div>
                <StyledLink to={`/dashboard/sars/${sar.id}`}>{sar.id}</StyledLink>
              </HeaderContainer>
            }
            dropdownMenu={
              !isReadonly && (
                <div
                  css={`
                    display: grid;
                    gap: ${Spacing[1]};
                    grid-template-columns: auto auto auto;
                    justify-content: flex-end;
                  `}
                >
                  <Button
                    id='sar-module-header__validate'
                    variant='basic'
                    disabled={isDirty || isReadonly}
                    onClick={(e) => {
                      e.stopPropagation();
                      validateForm?.();
                    }}
                  >
                    Validate
                  </Button>
                  {hasSARWritePermission ? (
                    <Button
                      id='sar-module-header__button--save'
                      variant='basic'
                      disabled={!isDirty || isReadonly}
                      onClick={(e) => {
                        e.stopPropagation();
                        submitForm?.();
                      }}
                    >
                      Save
                    </Button>
                  ) : (
                    <Tooltip
                      trigger={
                        <Button id='sar-module-header__button--save' variant='basic' disabled>
                          Save
                        </Button>
                      }
                    >
                      Your role does not have permission to edit SARs.
                    </Tooltip>
                  )}
                  <div>
                    <DropdownMenu
                      trigger={
                        <Button
                          id='sar-module-header__more'
                          width='icon'
                          variant='basic'
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <MoreIcon />
                        </Button>
                      }
                    >
                      <DropdownMenu.Item
                        id='sar-module-header__autofill'
                        onClick={(e: MouseEvent) => {
                          e.stopPropagation();
                          handleAutofillSAR(sar.id);
                        }}
                        disabled={!hasSARWritePermission || isReadonly}
                      >
                        Autofill attached objects
                      </DropdownMenu.Item>
                      <DropdownMenu.Item
                        id='sar-module-header__bsa-support-center'
                        role='link'
                        onClick={() => {
                          window.open(
                            'https://www.fincen.gov/frequently-asked-questions-regarding-fincen-suspicious-activity-report-sar',
                            '_blank',
                          );
                        }}
                      >
                        BSA Support Center
                      </DropdownMenu.Item>
                    </DropdownMenu>
                  </div>
                </div>
              )
            }
          >
            <TabbedSectionContainer>
              <StyledPageTabBar>
                {tabs.map(({ name }, idx) => (
                  <Tab
                    key={name}
                    selected={idx === selectedTabIdx}
                    id={name}
                    onClick={() => setSelectedTabIdx(idx)}
                  >
                    {name}
                  </Tab>
                ))}
              </StyledPageTabBar>
              {tabs.map((tab, idx) => (
                <TabContentContainer key={tab.name} isHidden={selectedTabIdx !== idx}>
                  {tab.contents}
                </TabContentContainer>
              ))}
            </TabbedSectionContainer>
          </ClickableDetail>
        );
      }}
    </JSONFormSAR>
  );
};
